import axios from 'axios';
import axiosCancel from 'axios-cancel';
import { stringify } from 'query-string';
import { offersConstants } from '../constants/offers';
import { statisticsConstants } from '../constants/statistics';
import { getBaseURL } from '../utils/utils';
import { API_VERSION } from '../config';
import { appConstants } from '../constants/app';
import { handleResponseErrors } from './ApiServiseHelpers';
import { offerTabsCategories } from '../fixtures/offerTabsCategories';

const baseURL = getBaseURL(PRODUCTION);

export const req = axios.create();
axiosCancel(req, { debug: false });

req.defaults.baseURL = baseURL;
req.defaults.withCredentials = true;
req.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';

req.defaults.xsrfCookieName = 'CSRF-TOKEN';
req.defaults.xsrfHeaderName = 'Z-CSRF-TOKEN';

req.interceptors.request.use((config) => {
  if (config.data instanceof FormData || config.timeout > 0) {
    return config;
  }

  config.data = stringify(config.data);
  return config;
});

const retryRequestInit = () => {
  let retryCount = 0;
  let timeout = 500;
  return (config) => {
    if (retryCount < 5) {
      retryCount++;
      timeout = timeout * 2;
      return req({ ...config, timeout });
    }
  };
};

const retryRequest = retryRequestInit();

req.interceptors.response.use(
  (response) => ({
    data: response.data.response,
    status: response.status,
    statusText: response.statusText,
    error: response.data.error || '',
  }),
  (error) => {
    handleResponseErrors(error);
    if (
      error.config &&
      error.response &&
      error.response.data &&
      error.response.data.error &&
      error.response.data.error[0] &&
      error.response.data.error[0].type === 'api.authorization.bad_csrf_token'
    ) {
      return retryRequest(error.config);
    }
    return Promise.reject(error);
  },
);

// TODO: Сделать все запросы отменяемыми

/** Вебмастер */
export const webmaster = {
  login(credentials) {
    return req.post('/login/', credentials).then((res) => res.data);
  },

  logout() {
    return req.post('/logout/').then((res) => res.data);
  },

  endOtherSessions() {
    return req.post(`/logout/other/`).then((res) => res.data);
  },

  confirmPhone(params) {
    return req.put(`/${API_VERSION}/profile/phone/`, params).then((res) => res.data);
  },

  getBasicInfo() {
    return req.get(`/${API_VERSION}/profile/info/`).then((res) => res.data);
    // return req.get(`/v2/profile/info/`).then(res => res.data);
  },

  setBasicInfo(data, config) {
    return req.put(`/${API_VERSION}/profile/info/`, data, config).then((res) => res.data);
    // return req.put(`/v2/profile/info/`, data, config).then(res => res.data);
  },

  getBalance() {
    return req.get(`/${API_VERSION}/profile/balance/`).then((res) => res.data);
  },

  getHold() {
    return req.get(`/${API_VERSION}/profile/hold/`).then((res) => res.data);
  },

  getWallets() {
    return req.get(`/${API_VERSION}/profile/wallets/`).then((res) => res.data);
  },

  deleteWallets(walletId) {
    return req.delete(`/${API_VERSION}/profile/wallets/${walletId}/`).then((res) => res.data);
  },

  setWallets(data) {
    return req.post(`/${API_VERSION}/profile/wallets/`, data).then((res) => res.data);
  },

  updateWallets({ wallet_id, ...params }) {
    return req
      .put(`/${API_VERSION}/profile/wallets/${wallet_id}/`, { ...params })
      .then((res) => res.data);
  },

  getManager() {
    return req.get(`/${API_VERSION}/profile/manager/`).then((res) => res.data);
  },

  changePassword(passwords) {
    return req.put(`/${API_VERSION}/profile/password_change/`, passwords).then((res) => res.data);
  },

  getNotify() {
    return req.get(`/${API_VERSION}/profile/notify/`).then((res) => res.data);
  },

  editNotify(notifications) {
    return req.put(`/${API_VERSION}/profile/notify/?${notifications}`).then((res) => res.data);
  },

  readMessage(params) {
    return req
      .put(`${baseURL}/${API_VERSION}/profile/readMessage/`, params)
      .then((res) => res.data);
  },

  requestOffer(offerId) {
    return req.post(`/${API_VERSION}/accounts/offers/${offerId}/`).then((res) => res.data);
  },

  refuseOffer(offerId) {
    return req.delete(`/${API_VERSION}/accounts/offers/${offerId}/`).then((res) => res.data);
  },

  setAvatar(data, { requestId, onUploadProgress = () => {}, ...params }) {
    return req
      .post(`/${API_VERSION}/accounts/avatar/`, data, {
        requestId,
        onUploadProgress,
        params: { ...params },
      })
      .then((res) => res.data);
  },

  addSocialAccount(data) {
    return req.post(`/${API_VERSION}/accounts/social/`, data).then((res) => res.data);
  },

  removeSocialAccount(accountId) {
    return req.delete(`/${API_VERSION}/accounts/social/${accountId}/`).then((res) => res.data);
  },

  confirnSMSPayout(params) {
    return req.put(`/${API_VERSION}/profile/sms_payout/`, params).then((res) => res.data);
  },

  getBots() {
    return req.get(`/${API_VERSION}/profile/bots/`).then((res) => res.data);
  },

  updateNovicePopupAmount(amount) {
    return req.put(`/${API_VERSION}/profile/popup_show/`, amount).then((res) => res.data);
  },

  activateTwoFactorAuth(params) {
    return req.put(`/${API_VERSION}/profile/two_factor/`, { ...params }).then((res) => res.data);
  },
};

/** Площадки */
export const sites = {
  create(site) {
    return req.post(`/${API_VERSION}/sites/`, site).then((res) => res.data);
  },

  getList(params) {
    return req.get(`/${API_VERSION}/sites/`, { params: { ...params } }).then((res) => res.data);
  },

  getSite(siteId) {
    return req.get(`/${API_VERSION}/sites/${siteId}/`).then((res) => res.data);
  },

  update(siteId, updates) {
    return req.put(`/${API_VERSION}/sites/${siteId}/`, updates).then((res) => res.data);
  },

  delete(siteId) {
    return req.delete(`/${API_VERSION}/sites/${siteId}/`).then((res) => res.data);
  },

  getSettings() {
    return req.get(`/${API_VERSION}/sites/settings/`).then((res) => res.data);
  },
};

/** Домены */
export const domains = {
  getList(params) {
    return req.get(`/${API_VERSION}/domains/`, { params: { ...params } }).then((res) => res.data);
  },

  getDomain(domainId, type) {
    return req.get(`/${API_VERSION}/domains/${type}/${domainId}/`).then((res) => res.data);
  },

  create(domain, type) {
    return req.post(`/${API_VERSION}/domains/${type}/`, domain).then((res) => res.data);
  },

  update(domainId, type, updates) {
    return req.put(`/${API_VERSION}/domains/${type}/${domainId}/`, updates).then((res) => res.data);
  },

  delete(domainId, type) {
    return req.delete(`/${API_VERSION}/domains/${type}/${domainId}/`).then((res) => res.data);
  },

  getSettings() {
    return req.get(`/${API_VERSION}/domains/settings/`).then((res) => res.data);
  },

  checkDomain(domain, type) {
    return req.post(`/${API_VERSION}/domains/cname/`, { domain, type }).then((res) => res.data);
  },
};

/** Категории */
export const categories = {
  getCategories() {
    return req.get(`/${API_VERSION}/offers/categories/`).then((res) => res.data);
  },
};

/** Офферы */
export const offers = {
  getTabs() {
    return new Promise((resolve, reject) => resolve(offerTabsCategories)).then((res) => res);
  },
  //
  // данные теперь приходят с запросом getList
  //
  // getFilters() {
  //   return req
  //     .get(`/${API_VERSION}/offers/filters/`, {
  //       requestId: offersConstants.SET_OFFER_FILTERS_REQUEST,
  //     })
  //     .then(res => res.data);
  // },

  getSources() {
    return req.get(`/${API_VERSION}/offers/sources/`).then((res) => res.data);
  },

  getList(params) {
    return req
      .get(`/${API_VERSION}/offers/`, {
        requestId: offersConstants.SET_OFFERS_LIST_REQUEST,
        params: { ...params },
      })
      .then((res) => res.data);
  },

  getSettings() {
    return req
      .get(`/${API_VERSION}/offers/settings/`, {
        requestId: appConstants.SET_OFFERS_SETTINGS_REQUEST,
      })
      .then((res) => res.data);
  },

  getOffer(offerId) {
    return req.get(`/${API_VERSION}/offers/${offerId}/`).then((res) => res.data);
  },

  //информация по офферу
  getOfferInfo(offerId) {
    return req
      .get(`/${API_VERSION}/offers/${offerId}/`, { params: { extended: 1, 'fields[]': 'info' } })
      .then((res) => res.data);
  },

  getOfferNews(offerId, params) {
    return req
      .get(`/${API_VERSION}/offers/${offerId}/news/`, { params: { ...params } })
      .then((res) => res.data);
  },

  getOfferFiles(offerId) {
    return req.get(`/${API_VERSION}/offers/${offerId}/files/`).then((res) => res.data);
  },

  getOfferPromo(offerId, params) {
    return req
      .get(`/${API_VERSION}/offers/${offerId}/news/promo/`, {
        params: { ...params },
      })
      .then((res) => res.data);
  },

  getOfferExtraGroups(offerId, params) {
    return req
      .get(`/${API_VERSION}/offers/${offerId}/promo/banners/`, {
        params: { ...params },
      })
      .then((res) => res.data);
  },

  getOfferExtraCollection(offerId, groupId) {
    return req.get(`/${API_VERSION}/offers/${offerId}/promo/${groupId}/`).then((res) => res.data);
  },

  searchOffer(params) {
    return req
      .get(`/${API_VERSION}/offers/search/`, { params: { ...params } })
      .then((res) => res.data);
  },

  getRecommendedOffers() {
    return req.get(`/${API_VERSION}/offers/recommendation/`).then((res) => res.data);
  },

  getLandingInfo(params) {
    return req
      .get(`/${API_VERSION}/offers/landing/info/`, { params: { ...params } })
      .then((res) => res.data);
  },

  sendLandingRequest(data) {
    return req.post(`/${API_VERSION}/offers/domain/`, data).then((res) => res.data);
  },
  getLandingDownloadLink(params) {
    return req
      .get(`/${API_VERSION}/streams/landings/pack/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getLandingDownloadItem(params) {
    return req
      .get(`/${API_VERSION}/streams/landings/download/`, { params: { ...params } })
      .then((res) => res.data);
  },

  sendOfferRequest(data) {
    return req.post(`/${API_VERSION}/offers/request/`, data).then((res) => res.data);
  },

  getOfferSupportHours(offerId, params) {
    return req
      .get(`/${API_VERSION}/offers/${offerId}/schedule/`, { params: { ...params } })
      .then((res) => res.data);
  },
};

/** Тикеты */
export const tickets = {
  create(ticket) {
    return req.post(`/${API_VERSION}/tickets/`, ticket).then((res) => res.data);
  },

  getList(params) {
    return req.get(`/${API_VERSION}/tickets/`, { params: { ...params } }).then((res) => res.data);
  },

  getTicket(ticketId) {
    return req.get(`/${API_VERSION}/tickets/${ticketId}/`).then((res) => res.data);
  },

  closeTicket(ticketId) {
    return req.get(`/${API_VERSION}/tickets/${ticketId}/close/`).then((res) => res.data);
  },

  getReplies(ticketId) {
    return req.get(`/${API_VERSION}/tickets/${ticketId}/replies/`).then((res) => res.data);
  },

  sendReply(ticketId, reply) {
    return req.post(`/${API_VERSION}/tickets/${ticketId}/replies/`, reply).then((res) => res.data);
  },

  getUnreadCount() {
    return req.get(`/${API_VERSION}/tickets/unread/`).then((res) => res.data);
  },

  setTicketRating({ ticketId, replyId, value }) {
    return req
      .put(`/${API_VERSION}/tickets/${ticketId}/rate/?replyId=${replyId}&value=${value}`)
      .then((res) => res.data);
  },

  setTicketHelp({ ticketId, replyId, value }) {
    return req
      .put(`/${API_VERSION}/tickets/${ticketId}/help/?replyId=${replyId}&value=${value}`)
      .then((res) => res.data);
  },
};

/** Новости */
export const news = {
  getList(params) {
    return req.get(`/${API_VERSION}/news/`, { params: { ...params } }).then((res) => res.data);
  },

  getMyNews(params) {
    return req
      .get(`/${API_VERSION}/news/`, { params: { type: 'my', ...params } })
      .then((res) => res.data);
  },

  getNewOffersNews(params) {
    return req
      .get(`/${API_VERSION}/news/`, { params: { type: 'new-offers', ...params } })
      .then((res) => res.data);
  },

  getNews(newsId) {
    return req.get(`/${API_VERSION}/news/${newsId}/`).then((res) => res.data);
  },

  getOfferNews(offerId) {
    return req.get(`/${API_VERSION}/news/offer/${offerId}/`).then((res) => res.data);
  },

  getPromo(params) {
    return req
      .get(`/${API_VERSION}/news/`, { params: { type: 'promo-all', ...params } })
      .then((res) => res.data);
  },

  getMyPromo(params) {
    return req
      .get(`/${API_VERSION}/news/`, { params: { type: 'promo-my', ...params } })
      .then((res) => res.data);
  },
};

/** Настройки */
export const settings = {
  getGlobals() {
    return req.get(`/${API_VERSION}/settings/`).then((res) => res.data);
  },

  getCountries() {
    return req.get(`/${API_VERSION}/geo/country/`).then((res) => res.data);
  },

  getSources() {
    return req.get(`/${API_VERSION}/offers/sources/`).then((res) => res.data);
  },

  getPublic() {
    return req.get(`/public/settings/`).then((res) => res.data);
  },

  getCSRFToken() {
    return req.get(`/csrf/`).then((res) => res.data);
  },
};

/** Потоки */
export const streams = {
  addStream(stream) {
    return req.post(`/${API_VERSION}/streams/`, stream).then((response) => response.data);
  },

  getStreams(params) {
    return req.get(`/${API_VERSION}/streams/`, { params: { ...params } }).then((res) => res.data);
  },

  getStream(streamId) {
    return req.get(`/${API_VERSION}/streams/${streamId}/`).then((res) => res.data);
  },

  update(streamId, updates) {
    return req.put(`/${API_VERSION}/streams/${streamId}/`, updates).then((res) => res.data);
  },

  removeStream(streamId) {
    return req.delete(`/${API_VERSION}/streams/${streamId}/`).then((res) => res.data);
  },

  getSettings() {
    return req.get(`/${API_VERSION}/streams/settings/`).then((res) => res.data);
  },

  getPostbackLogs(streamId, params) {
    return req
      .get(`/${API_VERSION}/streams/${streamId}/postback/log/`, { params: { ...params } })
      .then((res) => res.data);
  },

  checkPostback(postback) {
    return req.post(`/${API_VERSION}/postback/test/`, postback).then((res) => res.data);
  },

  getGlobalPostback() {
    return req.get(`/${API_VERSION}/postback/`).then((res) => res.data);
  },

  updateGlobalPostback(data) {
    return req.put(`/${API_VERSION}/postback/`, data).then((res) => res.data);
  },

  getGlobalPostbackLogs(params) {
    return req
      .get(`/${API_VERSION}/postback/logs/`, { params: { ...params } })
      .then((res) => res.data);
  },
  checkDirectLinkWithoutRedirectAvailability(params) {
    return req
      .get(`/${API_VERSION}/streams/tools/direct_link_without_redirect/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getLandingFeaturesSettings(params) {
    return req.get(`/${API_VERSION}/streams/config/landing/`, { params }).then((res) => res.data);
  },
};

/** TDS Потоки */
export const streamsTDS = {
  getStreamTDS(streamId) {
    return req.get(`/${API_VERSION}/streams/tds/${streamId}/`).then((res) => res.data);
  },
  updateTDS(streamId, updates) {
    return req.put(`/${API_VERSION}/streams/tds/${streamId}/`, updates).then((res) => res.data);
  },
  removeStreamTDS(streamId) {
    return req.delete(`/${API_VERSION}/streams/tds/${streamId}/`).then((res) => res.data);
  },
  getSettingsTDS() {
    return req.get(`/${API_VERSION}/streams/settings/tds/`).then((res) => res.data);
  },
  addStreamTDS(stream) {
    return req.post(`/${API_VERSION}/streams/tds/`, stream).then((response) => response.data);
  },
};

/** Финансы */
export const finances = {
  getPaymentsData(params) {
    return req
      .get(`/${API_VERSION}/finances/balance/`, { params: { ...params } })
      .then((res) => res.data);
  },

  getHoldDetails(params, currency = 'usd') {
    return req
      .get(`/${API_VERSION}/finances/hold/${currency}/`, { params: { ...params } })
      .then((res) => [currency, res.data]);
  },

  getHoldDetailsSettings(currency = 'usd') {
    return req
      .get(`/${API_VERSION}/finances/hold/${currency}/settings/`)
      .then((res) => [currency, res.data]);
  },

  getPaymentOrders(params) {
    return req
      .get(`/${API_VERSION}/finances/payment/`, { params: { ...params } })
      .then((res) => res.data);
  },

  createPayment(data) {
    return req.post(`/${API_VERSION}/finances/payment/`, data).then((res) => res.data);
  },

  instantPaymentOrder() {
    return req.post(`/${API_VERSION}/finances/payment/early/`).then((res) => res.data);
  },

  cancelPayment(id) {
    return req
      .post(`/${API_VERSION}/finances/payment/cancel/`, { payment_id: id })
      .then((res) => res.data);
  },
};

/** Статистика */
export const statistics = {
  getTabs(template) {
    return (
      req
        // .get(`/${API_VERSION}/statistics/tabs/`, { params: { template } })
        .get(`/${API_VERSION}/statistics/tabs/`, { params: { template: 7 } }) //оставляем только Детальную статистику
        .then((res) => res.data)
    );
  },

  getStats(type, params) {
    return req
      .get(`/${API_VERSION}/statistics/${type}/`, {
        requestId: statisticsConstants.SET_STATS_REQUEST,
        params: { ...params },
      })
      .then((res) => res.data);
  },

  getDictionary(type, params) {
    return req
      .post(`/${API_VERSION}/statistics/dictionary/${type}/`, { ...params })
      .then((res) => res.data);
  },

  getSubAccounts(subId, params) {
    return req
      .get(`/${API_VERSION}/statistics/subaccounts/${subId}/`, { params: { ...params } })
      .then((res) => res.data);
  },

  getWebmasterPresets() {
    return req.get(`/${API_VERSION}/statistics/webmaster/filters/`).then((res) => res.data);
  },

  addWebmasterPreset(filters) {
    return req
      .post(`/${API_VERSION}/statistics/webmaster/filters/`, filters)
      .then((res) => res.data);
  },

  updateWebmasterPreset(id, filters) {
    return req
      .put(`/${API_VERSION}/statistics/webmaster/filters/${id}/`, filters)
      .then((res) => res.data);
  },

  removeWebmasterPreset(id) {
    return req
      .delete(`/${API_VERSION}/statistics/webmaster/filters/${id}/`)
      .then((res) => res.data);
  },

  shareStatistics(type, params) {
    return req.post(`/${API_VERSION}/share/stats/${type}/`, params).then((res) => res.data);
  },
};

/** Дашборд */
export const dashboard = {
  getСharts(params) {
    const { period, category } = params;
    const generalRequestPath = `/${API_VERSION}/dashboard/charts/`;
    const spRequestPath = `/${API_VERSION}/dashboard/charts/sp`;
    return req
      .get(category === 'general' ? generalRequestPath : spRequestPath, { params: { period } })
      .then((res) => res.data);
  },

  getSummary() {
    return req.get(`/${API_VERSION}/dashboard/summary/`).then((res) => res.data);
  },

  getReferralsStats() {
    return req.get(`/${API_VERSION}/dashboard/referrals/`).then((res) => res.data);
  },

  getSettings() {
    return req.get(`/${API_VERSION}/dashboard/settings/`).then((res) => res.data);
  },

  updateSettings(settings) {
    return req.put(`/${API_VERSION}/dashboard/settings/`, settings).then((res) => res.data);
  },
};

/** FAQ */
export const faq = {
  getList() {
    return req.get(`/${API_VERSION}/faq/`).then((res) => res.data);
  },
  getTree() {
    return req.get(`/${API_VERSION}/faq/tree/`).then((res) => res.data);
  },
  getLastUpdate() {
    return req.get(`/${API_VERSION}/faq/last/`).then((res) => res.data);
  },
  getFaq(faqId) {
    return req.get(`/${API_VERSION}/faq/${faqId}/`).then((res) => res.data);
  },
  getGlossary() {
    return req.get(`/${API_VERSION}/faq/glossary/`).then((res) => res.data);
  },
  search(params) {
    return req
      .get(`/${API_VERSION}/faq/search/`, { params: { ...params } })
      .then((res) => res.data);
  },
  setVote(faqId, params) {
    return req.put(`/${API_VERSION}/faq/${faqId}/vote/`, params).then((res) => res.data);
  },
};

/** Агентские кабинеты */
export const agencies = {
  getList(params) {
    return req.get(`/${API_VERSION}/agencies/`, { params: { ...params } }).then((res) => res.data);
  },

  getCabinets(cabinetId) {
    return req.get(`/${API_VERSION}/agencies/${cabinetId}/cabinets/`).then((res) => res.data);
  },

  requestActivation(data) {
    return req.post(`/${API_VERSION}/agencies/`, data).then((res) => res.data);
  },

  sendPayment(cabinetId, data) {
    return req.post(`/${API_VERSION}/agencies/${cabinetId}/pay/`, data).then((res) => res.data);
  },

  getPaymentsList(params) {
    return req
      .get(`/${API_VERSION}/agencies/pay/list/`, { params: { ...params } })
      .then((res) => res.data);
  },

  getPersonalPromocode(agencyId) {
    return req.get(`/${API_VERSION}/agencies/${agencyId}/code/`).then((res) => res.data);
  },
};

/** Errors */
export const errors = {
  sendReport(report = {}) {
    return req.post(`/error/js/`, report).then((res) => res.data);
  },
};

/** Multi */
export const multi = {
  dashboard(resources) {
    return req.post(`/${API_VERSION}/multi/`, resources).then((res) => res.data);
  },
};

export const referalls = {
  getList(params) {
    return req
      .get(`/${API_VERSION}/accounts/referrals/`, { params: { ...params } })
      .then((res) => res.data);
  },
};

/** Чат с личным менеджером */
export const chat = {
  // getRoomParams() {
  //   return axios
  //     .get(`${baseURL}/${API_VERSION}/rocketchat/channel/`, { withCredentials: true })
  //     .then(res => res.data);
  // },
  sendMessage(message) {
    return req.post(`/${API_VERSION}/livechat/`, { message });
  },
  getHistory(lastId) {
    return req.get(`/${API_VERSION}/livechat/`, { params: { lastId } });
  },
  resetUnreadCounter() {
    return req.put(`${API_VERSION}/livechat/read/`);
  },
};

/** Мастер-аккаунт */
export const slaves = {
  getSlaves(params) {
    return axios
      .get(`${baseURL}/${API_VERSION}/profile/slave/`, {
        params: { ...params },
        withCredentials: true,
      })
      .then((res) => res.data);
  },
  getSlave(type, params) {
    return axios
      .get(`${baseURL}/${API_VERSION}/profile/slave/stats/${type}/`, {
        params: { ...params },
        withCredentials: true,
      })
      .then((res) => res.data);
  },
  getSlavesStats(type, params) {
    return axios
      .get(`${baseURL}/${API_VERSION}/profile/slave/stats/${type}/`, {
        params: { ...params },
        withCredentials: true,
      })
      .then((res) => res.data);
  },
  deleteSlave(id) {
    return req.delete(`${baseURL}/${API_VERSION}/profile/slave/${id}/`).then((res) => res.data);
  },
  getSlavePaymentOrders({ id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/payment/`, {
        params: { ...params },
        withCredentials: true,
      })
      .then((res) => res.data);
  },
  instantSlavePaymentOrder({ id, ...params }) {
    return req
      .post(`/${API_VERSION}/profile/slave/${id}/payment/early/`, params)
      .then((res) => res.data);
  },
  createSlavePayment({ id, ...params }) {
    return req.post(`/${API_VERSION}/profile/slave/${id}/payment/`, params).then((res) => res.data);
  },
  getSlaveWallets(id) {
    return req.get(`/${API_VERSION}/profile/slave/${id}/wallets/`).then((res) => res.data);
  },
  setSlaveWallets({ id, ...data }) {
    return req.post(`/${API_VERSION}/profile/slave/${id}/wallets/`, data).then((res) => res.data);
  },
  getSlaveWalletsBalance(id) {
    return req.get(`/${API_VERSION}/profile/slave/${id}/wallets/balance/`).then((res) => res.data);
  },
  // для чего это
  // getSlaveBalance(id) {
  //   return req.get(`/${API_VERSION}/profile/slave/${id}/balance/`).then(res => res.data);
  // },
  getSlavePaymentsData({ id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/balance/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getSlaveOffersList({ id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/offers/`, {
        requestId: offersConstants.SET_OFFERS_LIST_REQUEST,
        params: { ...params },
      })
      .then((res) => res.data);
  },
  getSlaveOffersListMulti(id, params) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/offers/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getSlaveStreams({ id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/streams/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getSlaveSiteList({ id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/sites/`, { params: { ...params } })
      .then((res) => res.data);
  },
  getSlaveSiteSettings(id) {
    return req.get(`/${API_VERSION}/profile/slave/${id}/sites/settings/`).then((res) => res.data);
  },
  getSlaveDictionary(type, { id, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/stats/dictionary/${type}/`, {
        params: { ...params },
      })
      .then((res) => res.data);
  },
  getSlaveDictionaryMulti(type, params) {
    return req
      .post(`/${API_VERSION}/profile/slave/stats/dictionary/${type}/`, { ...params })
      .then((res) => res.data);
  },
  getSlaveSubAccounts(subId, { slaveId, ...params }) {
    return req
      .get(`/${API_VERSION}/profile/slave/${slaveId}/stats/subaccounts/${subId}/`, {
        params: { ...params },
      })
      .then((res) => res.data);
  },
  getSlaveHoldDetails({ id, ...params }, currency = 'usd') {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/hold/${currency}/`, { params: { ...params } })
      .then((res) => [currency, res.data]);
  },

  getSlaveHoldDetailsSettings(currency = 'usd', id) {
    return req
      .get(`/${API_VERSION}/profile/slave/${id}/hold/${currency}/settings/`)
      .then((res) => [currency, res.data]);
  },
  // подключение к мастер-аккаунту
  connectAsSlave(hash) {
    return req.post(`/${API_VERSION}/profile/slave/${hash}/`).then((res) => res.data);
  },
  deleteSlaveWallets({ id, slaveId }) {
    return req
      .delete(`/${API_VERSION}/profile/slave/${slaveId}/wallets/${id}/`)
      .then((res) => res.data);
  },

  updateSlaveWallets({ id, wallet_id, ...params }) {
    return req
      .put(`/${API_VERSION}/profile/slave/${id}/wallets/${wallet_id}/`, { ...params })
      .then((res) => res.data);
  },
};

/** Договоры ИП и ООО */
export const contracts = {
  getContracts(type) {
    return req.get(`/${API_VERSION}/contracts/${type}/`).then((res) => res.data);
  },
  deleteContract(type, id) {
    return req.delete(`/${API_VERSION}/contracts/${type}/${id}/`).then((res) => res.data);
  },
  saveContract(type, data) {
    return req.put(`/${API_VERSION}/contracts/${type}/${data.id}/`, data).then((res) => res.data);
  },
  approveContract(type, data) {
    return req
      .put(`/${API_VERSION}/contracts/${type}/${data.id}/approve/`, data)
      .then((res) => res.data);
  },
  addContract(type, data) {
    return req.post(`/${API_VERSION}/contracts/${type}/`, data).then((res) => res.data);
  },
};

/** INFOCDN */
export const infocdn = {
  getNewsShowcaseGeoList() {
    return req.get(`/infocdn/news/geo/`).then((res) => res.data);
  },

  getNewsShowcaseThemesList() {
    return req.get(`/infocdn/news/topics/`).then((res) => res.data);
  },
};

// Промокоды
export const promo = {
  checkPromoCode(code) {
    return req.post(`/${API_VERSION}/webmaster/promo/check/`, code).then((res) => res.data);
  },
  getPromoHistory() {
    return req.get(`/${API_VERSION}/webmaster/promo/`).then((res) => res.data);
  },
  addActivatePromoCode(params) {
    return req.post(`/${API_VERSION}/webmaster/promo/activate/`, params).then((res) => res.data);
  },
};

export const cash = {
  fetchData(params) {
    return req.post(`/${API_VERSION}/contest/cashout/`, params);
  },
};

export const party = {
  requestTicket(params) {
    return req.post(`/${API_VERSION}/convert/make/`, params);
  },
};

// ** Лей через приложение **
export const application = {
  getApplications() {
    return req.get(`/${API_VERSION}/webmaster/application/`).then((res) => res.data);
  },
  getApplicationDetail(appId) {
    return req.get(`/${API_VERSION}/webmaster/application/view/${appId}/`).then((res) => res.data);
  },
  createDeeplink(params) {
    return req
      .post(`/${API_VERSION}/webmaster/application/deeplink/create/`, params)
      .then((res) => res.data);
  },
  addAccounts(params) {
    return req
      .post(`/${API_VERSION}/webmaster/application/account/create/`, { ...params })
      .then((res) => res.data);
  },
};

// Кабинеты Тик-Ток
export const ttcabinets = {
  getSelectOptions() {
    return req.get(`/${API_VERSION}/agencies/tiktok/vocabulary/`).then((res) => res.data);
  },
  getCabinetsList() {
    return req.get(`/${API_VERSION}/agencies/tiktok/list/`).then((res) => res.data);
  },
  addCabinetRequest(params) {
    return req.post(`/${API_VERSION}/agencies/tiktok/`, { ...params }).then((res) => res.data);
  },
};

// Кабинеты Тик-Ток RUB
export const ttcabinetsRUB = {
  getSelectOptionsRUB() {
    return req.get(`/${API_VERSION}/agencies/tiktokrub/vocabulary/`).then((res) => res.data);
  },
  getCabinetsListRUB() {
    return req.get(`/${API_VERSION}/agencies/tiktokrub/list/`).then((res) => res.data);
  },
  addCabinetRequestRUB(params) {
    return req.post(`/${API_VERSION}/agencies/tiktokrub/`, { ...params }).then((res) => res.data);
  },
};

const ApiService = {
  webmaster,
  sites,
  offers,
  categories,
  tickets,
  news,
  settings,
  finances,
  streams,
  statistics,
  domains,
  faq,
  dashboard,
  errors,
  referalls,
  chat,
  slaves,
  contracts,
  infocdn,
};

export default ApiService;
